/**
 * Cart Initialling — service/parent linking.
 *
 * When a customer adds a personalisation service (e.g. "Service · Initialling")
 * alongside a luggage/product item, the service line carries:
 *   data-linked-title = parent product's title (via item.properties.Title)
 * The parent product line carries:
 *   data-line-title   = its own item.title
 *   item.properties.Personalisation_Text / Position / Size / Font / Color
 *
 * This script keeps the two lines in sync on removal:
 *
 *   FORWARD  service line removed → clear parent's Personalisation_* props
 *            (so the personalisation doesn't linger orphaned)
 *
 *   REVERSE  parent product removed → also remove its attached service line
 *            (so the customer isn't charged for personalisation alone)
 *
 * Both listeners are fail-safe: any error falls back to letting the theme's
 * native remove behaviour run, so removal is never silently swallowed.
 */
(function () {
  'use strict';

  var REMOVE_SELECTOR = '[is="cart-remove-item"]';
  var SERVICE_CLASS = 'cart-item--service';
  var PERSONALISATION_PREFIX = 'Personalisation_';

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
      return r.json();
    });
  }

  function changeLine(id, payload) {
    var body = Object.assign({ id: id }, payload);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart change failed: ' + r.status);
      return r.json();
    });
  }

  function refreshCart() {
    // Try the theme's web-component refresh if exposed; otherwise dispatch a
    // synthetic cart:refresh event that the theme cart drawer listens for.
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      // dawn-style drawer
      try { drawer.renderContents({ sections: null }); } catch (_) {}
    }
    // As a last resort the theme will re-render on next add or page change.
  }

  function findServiceLineFor(parentTitle) {
    if (!parentTitle) return null;
    var services = document.querySelectorAll('.cart-item.' + SERVICE_CLASS);
    for (var i = 0; i < services.length; i++) {
      if (services[i].dataset.linkedTitle === parentTitle) return services[i];
    }
    return null;
  }

  function findParentLineFor(parentTitle) {
    if (!parentTitle) return null;
    var lines = document.querySelectorAll('.cart-item:not(.' + SERVICE_CLASS + ')');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].dataset.lineTitle === parentTitle) return lines[i];
    }
    return null;
  }

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest ? event.target.closest(REMOVE_SELECTOR) : null;
    if (!btn) return;

    var line = btn.closest('.cart-item');
    if (!line) return;

    var isService = line.classList.contains(SERVICE_CLASS);

    if (isService) {
      // FORWARD — service line being removed.
      // Let the theme remove the service normally; we just clear the parent's
      // Personalisation_* props afterwards. This is asynchronous and best-effort —
      // never block the user's remove click.
      var parentTitle = line.dataset.linkedTitle;
      if (!parentTitle) return;

      // Fire-and-forget after a short delay so the theme's removal POST goes first.
      setTimeout(function () {
        fetchCart().then(function (cart) {
          var parent = (cart.items || []).find(function (it) { return it.title === parentTitle; });
          if (!parent || !parent.properties) return;

          var newProps = {};
          var changed = false;
          Object.keys(parent.properties).forEach(function (k) {
            if (k.indexOf(PERSONALISATION_PREFIX) === 0) {
              changed = true; // strip it
            } else {
              newProps[k] = parent.properties[k];
            }
          });
          if (!changed) return;

          return changeLine(parent.key, { properties: newProps }).then(refreshCart);
        }).catch(function (err) {
          console.warn('[cart-initialling] forward sync failed:', err);
        });
      }, 250);

      return; // do not preventDefault — let theme handle the service removal
    }

    // REVERSE — non-service line (the parent product) being removed.
    // If a matching service line exists AND this parent carries Personalisation_*
    // props, we take over: remove both lines via cart-change, then refresh.

    var lineTitle = line.dataset.lineTitle;
    if (!lineTitle) return;

    var service = findServiceLineFor(lineTitle);
    if (!service) return; // nothing attached — let theme do its thing

    // Intercept the click — we'll handle the dual-remove ourselves.
    event.preventDefault();
    event.stopPropagation();

    fetchCart().then(function (cart) {
      // Re-locate by title (DOM index can drift between the click and the fetch).
      var parent = (cart.items || []).find(function (it) { return it.title === lineTitle; });
      if (!parent) throw new Error('parent not found in cart');

      var hasPersonalisation = parent.properties && Object.keys(parent.properties).some(function (k) {
        return k.indexOf(PERSONALISATION_PREFIX) === 0;
      });

      // If parent doesn't carry personalisation props (e.g. a duplicate non-personalised
      // copy of the same product), leave the service alone — just remove the parent.
      if (!hasPersonalisation) {
        return changeLine(parent.key, { quantity: 0 }).then(refreshCart);
      }

      // Find the matching service line in the cart by linked title (its Title property).
      var serviceItem = (cart.items || []).find(function (it) {
        return it.properties && it.properties.Title === lineTitle;
      });
      if (!serviceItem) {
        // Service vanished between DOM render and now — just remove parent.
        return changeLine(parent.key, { quantity: 0 }).then(refreshCart);
      }

      // Remove service first, then parent. Using line keys so order doesn't drift.
      return changeLine(serviceItem.key, { quantity: 0 })
        .then(function () { return changeLine(parent.key, { quantity: 0 }); })
        .then(refreshCart);
    }).catch(function (err) {
      console.warn('[cart-initialling] reverse remove failed, falling back to native remove:', err);
      // Re-trigger the original click without our listener interfering.
      btn.removeEventListener('click', arguments.callee);
      btn.click();
    });
  }, true); // capture phase — fires before theme's default handler so we can intercept
})();
